import { motion, AnimatePresence } from 'framer-motion';
import { HiCheckCircle, HiChevronRight } from 'react-icons/hi';
import { FaTrophy } from 'react-icons/fa';

// 7-step purple palette and canonical level names (levels 1..7)
const levelColors = [
  '#E9D5FF',
  '#D6BCFA',
  '#B794F4',
  '#9F7AEA',
  '#7C3AED',
  '#6D28D9',
  '#4C1D95'
];

const levelNames = [
  'AI Novice',
  'AI Explorer',
  'AI Practitioner',
  'AI Specialist',
  'AI Expert',
  'AI Master',
  'AI Guru'
];

const LevelCompletionModal = ({ 
  isOpen, 
  level, 
  score, 
  totalQuestions, 
  onNextLevel,
  isLastLevel = false 
}) => {
  if (!isOpen) return null;

  const levelIndex = level - 1;
  const levelColor = levelColors[levelIndex] || '#8B5CF6';
  const levelName = levelNames[levelIndex] || `Level ${level}`;
  const percentage = ((score / totalQuestions) * 100).toFixed(0);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        style={{ backgroundColor: 'rgba(0, 0, 0, 0.6)' }}
      >
        {/* Modal Container */}
        <motion.div
          initial={{ scale: 0.8, opacity: 0, y: 50 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.8, opacity: 0, y: 50 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Decorative Top Gradient */}
          <div
            className="h-2"
            style={{
              background: `linear-gradient(90deg, ${levelColor}, ${levelColor}dd)`
            }}
          />

          {/* Content */}
          <div className="p-8 text-center">
            {/* Success Icon with Animation */}
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: 'spring', damping: 15 }}
              className="mb-6 flex justify-center"
            >
              <div
                className="w-24 h-24 rounded-full flex items-center justify-center shadow-lg"
                style={{ backgroundColor: levelColor }}
              >
                {isLastLevel ? (
                  <FaTrophy className="text-white text-5xl" />
                ) : (
                  <HiCheckCircle className="text-white text-5xl" />
                )}
              </div>
            </motion.div>

            {/* Title */}
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-3xl font-bold text-gray-900 mb-2"
            >
              {isLastLevel ? '🎉 Congratulations!' : 'Level Completed!'}
            </motion.h2>

            {/* Level Name */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-6"
            >
              <div
                className="inline-block px-4 py-2 rounded-full text-white font-semibold text-lg"
                style={{ backgroundColor: levelColor }}
              >
                {levelName}
              </div>
            </motion.div>

            {/* Score Display */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5 }}
              className="mb-6 space-y-2"
            >
              <div className="text-5xl font-bold" style={{ color: levelColor }}>
                {percentage}%
              </div>
              <div className="text-gray-600">
                {score} out of {totalQuestions} correct
              </div>
            </motion.div>

            {/* Motivational Message */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
              className="text-gray-600 mb-8"
            >
              {isLastLevel 
                ? "You've completed all levels! You're an AI expert!"
                : "Great job! Ready for the next challenge?"
              }
            </motion.p>

            {/* Action Button */}
            <motion.button
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onNextLevel}
              className="w-full py-4 px-6 rounded-xl bg-primary hover:bg-primary-dark text-white font-semibold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
            >
              <span>{isLastLevel ? 'View Results' : 'Move to Next Level'}</span>
              <HiChevronRight className="text-xl" />
            </motion.button>

            {/* Decorative Elements */}
            <div className="absolute top-8 right-8 opacity-8">
              <FaTrophy className="text-6xl" style={{ color: levelColor }} />
            </div>
            <div className="absolute bottom-8 left-8 opacity-8">
              <HiCheckCircle className="text-6xl" style={{ color: levelColor }} />
            </div>
          </div>

          {/* Decorative Bottom Gradient */}
          <div
            className="h-2"
            style={{
              background: `linear-gradient(90deg, ${levelColor}dd, ${levelColor})`
            }}
          />
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default LevelCompletionModal;
